import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useListGateways } from 'src/features/gateway/api/useListGateways';
import { useListZones } from 'src/features/zone/api/useListZones';
import { AllowListFormSchemaValues } from 'src/features/allowList/utils/formSchema';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectTrigger,
    MultiSelectOption,
} from 'src/registry/ui/MultiSelect';
import { generalMessages } from 'src/registry/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';

type AllowListFormProps = {
    form: UseFormReturn<AllowListFormSchemaValues>;
    onSubmit: (values: AllowListFormSchemaValues) => void;
};

export default function AllowListForm({ onSubmit, form }: AllowListFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { data: zonesData } = useListZones({ parkingId });
    const { data: gatewaysData } = useListGateways({ parkingId });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<AllowListFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(generalMessages.name)}
            />
            <TextareaFormField<AllowListFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(generalMessages.description)}
            />
            <FormField
                control={form.control}
                name="zones"
                render={({ field }) => {
                    const fieldValue = Array.isArray(field.value) ? field.value : [];

                    const fieldData = fieldValue
                        .map(
                            (value) =>
                                zonesData?.results
                                    .find((option) => option.id === value)
                                    ?.translations.find((translation) => translation.language === locale)?.name,
                        )
                        .join(', ');

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...allowListMessages.zones} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect onChange={field.onChange} value={field.value ?? ''}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl>
                                                {fieldData || (
                                                    <span className="text-gray-400">
                                                        {formatMessage(allowListMessages.zones)}...
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent>
                                            {zonesData?.results?.map((option) => (
                                                <MultiSelectOption key={option.id} selected={option.id}>
                                                    {
                                                        option.translations.find(
                                                            (translation) => translation.language === locale,
                                                        )?.name
                                                    }
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={form.control}
                name="allowed_gateways"
                render={({ field }) => {
                    const fieldValue = Array.isArray(field.value) ? field.value : [];

                    const fieldData = fieldValue
                        .map(
                            (value) =>
                                gatewaysData?.results
                                    .find((option) => option.id === value)
                                    ?.translations.find((translation) => translation.language === locale)?.name,
                        )
                        .join(', ');

                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog">
                                <FormattedMessage {...allowListMessages.gateways} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect onChange={field.onChange} value={field.value ?? ''}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl>
                                                {fieldData || (
                                                    <span className="text-gray-400">
                                                        {formatMessage(allowListMessages.gateways)}...
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent>
                                            {gatewaysData?.results?.map((option) => (
                                                <MultiSelectOption key={option.id} selected={option.id}>
                                                    {
                                                        option.translations.find(
                                                            (translation) => translation.language === locale,
                                                        )?.name
                                                    }
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />
        </Form>
    );
}
